import React, { useEffect } from "react";
import PropTypes from "prop-types";
import get from "lodash.get";
import { useForm } from "react-hook-form";

import { projectDataPropType } from "../util/propTypeHelper";

/**
 * @param {string} arrayPropertyPath path where we should store the form data inside the array that lives at the storageKey path.
 * For example, if we have an array of objects that live at "intersections[0].delay", and we want to save the form data there, the
 * storageKey would be "intersections" and the arrayPropertyPath would be "delay"
 * @param {function} children React elements passed inside StepForm
 * @param {function} onFormSubmit
 * @param {Object} projectData
 * @param {function} saveFormData used to update the projectData object
 * @param {function} setIsDirty a hack Elliot implemented to be able to access the isDirty field from parent components to a StepForm. Indicates a poor ux or component design that should be refactored
 * @param {string} storageKey dot-separated path that the data will live in projectData. ie: "projectInformation" or "trafficVolumes.intersection1"
 * @param {Object} [style]
 */
export default function StepForm({
  arrayPropertyPath = "",
  children,
  onFormSubmit,
  projectData,
  saveFormData,
  setIsDirty = () => {},
  storageKey,
  style,
  isOnChangeMode = true,
}) {
  const getDefaultValues = () => {
    const baseDefaultValues = get(projectData, storageKey) || {};

    if (arrayPropertyPath) {
      return baseDefaultValues.reduce(
        (acc, values) => ({
          ...acc,
          [values.id]: get(values, arrayPropertyPath),
        }),
        {}
      );
    }
    return baseDefaultValues;
  };
  const defaultValues = getDefaultValues();

  const {
    clearErrors,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    reset: resetForm,
    setError,
    watch,
  } = useForm({ defaultValues, mode: isOnChangeMode ? "onChange" : "onBlur" });

  /**
   * fires a saveFormData() call for each entry in the formData object
   * we look at each key in formData and find the index of the matching element in projectData
   * then we save the value at the arrayPropertyPath inside that element
   * @param {object} formData
   */
  const saveDataToEachObjectInArray = (formData) => {
    Object.entries(formData).forEach(async ([id, value]) => {
      // find the object in the array that has the same id as the form data
      const index = projectData[storageKey].findIndex((item) => item.id === id);
      const paramString = `${storageKey}[${index}].${arrayPropertyPath}`;
      const existingValue = get(projectData, paramString);
      await saveFormData({
        paramString,
        value: { ...existingValue, ...value },
      });
    });
  };

  const handleFormSubmit = (formData) => {
    if (arrayPropertyPath) {
      saveDataToEachObjectInArray(formData);
    } else {
      // save form data in an object at the storageKey path like normal
      saveFormData({
        paramString: storageKey,
        value: formData,
      });
    }
    onFormSubmit(formData);
  };

  // call setIsDirty each time the form changes from dirty to clean or vice versa
  // TODO fix this - this is a hack and should be refactored so the isDirty logic can't be set from outside the component
  useEffect(() => setIsDirty(isDirty), [isDirty, setIsDirty]);
  const formData = watch();
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} style={style}>
      {children({
        clearErrors,
        defaultValues,
        errors,
        formData,
        isDirty,
        isValid,
        register,
        resetForm,
        setError,
      })}
    </form>
  );
}

StepForm.propTypes = {
  arrayPropertyPath: PropTypes.string,
  children: PropTypes.func.isRequired,
  isOnChangeMode: PropTypes.bool,
  onFormSubmit: PropTypes.func.isRequired,
  projectData: projectDataPropType.isRequired,
  saveFormData: PropTypes.func.isRequired,
  setIsDirty: PropTypes.func,
  storageKey: PropTypes.string.isRequired,
  style: PropTypes.object,
};
